/**
 * ヘッダーのお気に入り（.action__wishlist）を速くする＋件数バッジを出す。
 *
 * 前提（2026-08-04 実測）:
 * Swym Wishlist Plus は storefrontLayoutType が as-section のため、お気に入りUIは
 * /pages/swym-wishlist に描かれる。ドロワーやモーダルは存在せず、`swat.ui.open()` を
 * 直接呼んでも同じページへ遷移する（Swym 注入の a.swym-advanced-header-icon も同様）。
 * つまり「遷移せずに開く」経路は無い。
 *
 * 遅さの内訳は、遷移先ページの読み込み（script 188本・DCL warm 1.2s / cold 3.9s）と、
 * その後に走る swymrelay の直列4段（script-tag-check → checkAndGet → fetch-lists → count）。
 * どちらもクリック後に始まるので、クリック前に始めてしまうのがいちばん効く。
 *
 * → ハートに指が乗った時点で遷移先を prerender（Speculation Rules）する。
 *   非対応ブラウザには <link rel="prefetch"> でHTMLだけ先に取る。
 *   遷移先には sections/swym-wishlist.liquid のスケルトンが出るので、待ち時間は空白ではなくなる。
 */
(() => {
  const LINK = 'a.action__wishlist';
  const TARGET = '/pages/swym-wishlist';

  /** Speculation Rules（Chrome 系）。hover 相当の moderate で prerender する。 */
  const addSpeculationRules = () => {
    if (!HTMLScriptElement.supports || !HTMLScriptElement.supports('speculationrules')) return false;
    if (document.querySelector('script[type="speculationrules"][data-wishlist]')) return true;

    const script = document.createElement('script');
    script.type = 'speculationrules';
    script.dataset.wishlist = '';
    script.textContent = JSON.stringify({
      prerender: [{ where: { href_matches: TARGET }, eagerness: 'moderate' }],
    });
    document.head.appendChild(script);
    return true;
  };

  /** フォールバック: 初回の hover / touch で HTML だけ先に取る。 */
  const prefetchOnce = () => {
    if (document.querySelector('link[rel="prefetch"][data-wishlist]')) return;
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = TARGET;
    link.dataset.wishlist = '';
    document.head.appendChild(link);
  };

  const supported = addSpeculationRules();

  if (!supported) {
    const warm = (event) => {
      if (!event.target.closest?.(LINK)) return;
      prefetchOnce();
    };
    document.addEventListener('pointerenter', warm, { capture: true, passive: true });
    document.addEventListener('touchstart', warm, { capture: true, passive: true });
    document.addEventListener('focusin', warm, { capture: true, passive: true });
  }

  /**
   * 件数バッジ。
   *
   * Swym は件数を注入アイコン側（.swym-header-icon-count）に描くが、そのアイコンは
   * cart-drawer-component の中＝DOM 最下部に注入されていて画面に出ないので、
   * テーマ側のハートに写して見えるようにしている。
   *
   * 参照元は1つでは足りない（実測でどちらも起きる）:
   *   - 通常ページ: .swym-header-icon-count は出るが、値が入るまで約1秒かかり、
   *     その間 Swym 自身が "0" を描くのでバッジが一瞬消える
   *   - /pages/swym-wishlist: .swym-header-icon-count が**そもそも描かれない**ため、
   *     DOM だけを見ているとバッジが消える
   * → Swym が localStorage に持つキャッシュ（swym-header-display-count-<shop>）を
   *   初期値に使い、DOM の値は「確定値」として上書きに使う。参照元が無いときは
   *   最後に判った件数を保持して消さない（0 が確定したときだけ消す）。
   */
  const CACHE_PREFIX = 'swym-header-display-count-';

  /** @returns {number|null} 取得できなければ null（=不明。バッジは触らない） */
  const readCachedCount = () => {
    try {
      for (const key of Object.keys(localStorage)) {
        if (!key.startsWith(CACHE_PREFIX)) continue;
        const value = parseInt(localStorage.getItem(key) ?? '', 10);
        if (!Number.isNaN(value)) return value;
      }
    } catch {
      // Safari のプライベートモード等で localStorage が触れないことがある
    }
    return null;
  };

  /** @returns {number|null} Swym が描いた確定値。要素が無い/空なら null */
  const readRenderedCount = () => {
    const source = document.querySelector('.swym-header-icon-count, .swym-header-icon-count-bubble');
    if (source) {
      const digits = source.textContent.replace(/\D+/g, '');
      if (digits !== '') return parseInt(digits, 10);
    }
    // お気に入り一覧ページでは一覧側の「N アイテム」が唯一の確定値になる
    const listTotal = document.querySelector('.swym-storefront-layout-total-list-items-count');
    if (listTotal) {
      const digits = listTotal.textContent.replace(/\D+/g, '');
      if (digits !== '') return parseInt(digits, 10);
    }
    return null;
  };

  let lastCount = null;

  const paint = (count) => {
    const link = document.querySelector(LINK);
    if (!link) return;

    let bubble = link.querySelector('.wishlist-bubble');
    if (!count) {
      if (bubble) bubble.remove();
      return;
    }
    if (!bubble) {
      bubble = document.createElement('span');
      bubble.className = 'wishlist-bubble';
      bubble.setAttribute('aria-hidden', 'true');
      link.appendChild(bubble);
    }
    const text = count > 99 ? '99+' : String(count);
    if (bubble.textContent !== text) bubble.textContent = text;
  };

  const sync = () => {
    const rendered = readRenderedCount();
    const count = rendered !== null ? rendered : lastCount !== null ? lastCount : readCachedCount();
    if (count === null) return; // 不明なうちは前の表示を保つ
    lastCount = count;
    paint(count);
  };

  // まずキャッシュで即描く（Swym の初期化を待たない）
  sync();

  let queued = false;
  const scheduleSync = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      sync();
    });
  };

  // Swym はアイコンも一覧も後から注入・差し替えるので、特定の要素ではなく body を見る。
  // 消えたり生え直したりしても追従できる。
  new MutationObserver(scheduleSync).observe(document.body, {
    childList: true,
    subtree: true,
    characterData: true,
  });

  // 別タブでの追加・削除にも追従する
  window.addEventListener('storage', (event) => {
    if (event.key && event.key.startsWith(CACHE_PREFIX)) scheduleSync();
  });

  document.addEventListener('DOMContentLoaded', sync);
  for (const ms of [300, 800, 1500, 3000, 6000, 10000]) setTimeout(sync, ms);
})();
